import { Plural, Trans, useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import type { LotoAttachmentGroup } from 'features/lotoReports/lotoAttachmentGroups'
import type {
  LotoIsolationPoint,
  LotoReport,
  LotoReportScope,
} from 'features/lotoReports/lotoReportSchemas'
import { targetEquipmentText } from 'features/lotoReports/lotoTargetEquipment'
import { useLotoAttachedPages } from 'features/lotoReports/useLotoAttachedPages'
import {
  type LotoProposalSaveState,
  useLotoProposalSave,
} from 'features/lotoReports/useLotoProposalSave'
import type { ReactElement } from 'react'
import styled from 'styled-components'

export type OpenLotoPage = (
  document: DocumentTableRow,
  pageNumber: number
) => void

type LotoProposalCardProps = {
  scope: LotoReportScope
  proposal: LotoReport
  onOpenPage: OpenLotoPage
}

const ProposalShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 0.0625rem solid var(--border);
  border-radius: var(--radius-lg);
  color: var(--foreground);
`

const ProposalList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: 0;
  padding-left: 1.25rem;
  list-style: decimal;

  & > li::marker {
    color: var(--muted-foreground);
    font-size: 0.875rem;
  }
`

const DrawingList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
`

const DrawingRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
  min-width: 0;
`

function PointItem({ point }: { point: LotoIsolationPoint }): ReactElement {
  const { t } = useLingui()
  return (
    <li>
      <Box display='flex' align='baseline' gap='sm' wrap='wrap'>
        <Text as='span' variant='label'>
          {point.tag || t`Untagged point`}
        </Text>
        {point.description && (
          <Text as='span' variant='caption' color='muted'>
            {point.description}
          </Text>
        )}
        {point.isolation_position && (
          <Text as='span' variant='caption'>
            {point.isolation_position}
          </Text>
        )}
      </Box>
    </li>
  )
}

function DrawingItem({
  group,
  onOpenPage,
}: {
  group: LotoAttachmentGroup
  onOpenPage: OpenLotoPage
}): ReactElement {
  const { t } = useLingui()
  const { document } = group
  return (
    <DrawingRow>
      <Text as='span' variant='label'>
        {group.number ?? group.title ?? t`Drawing`}
      </Text>
      {group.pages.map(({ pageId, target }) => {
        if (!target)
          return (
            <Text key={pageId} as='span' variant='caption' color='muted'>
              {t`Page unavailable`}
            </Text>
          )
        const { pageNumber } = target
        if (!document)
          return (
            <Text key={pageId} as='span' variant='caption' color='muted'>
              {t`Page ${pageNumber}`}
            </Text>
          )
        return (
          <Button
            key={pageId}
            type='button'
            variant='outline'
            size='sm'
            onClick={() => onOpenPage(document, pageNumber)}>
            {t`Open page ${pageNumber}`}
          </Button>
        )
      })}
    </DrawingRow>
  )
}

function SaveAction({
  state,
  onSave,
}: {
  state: LotoProposalSaveState
  onSave: () => void
}): ReactElement {
  if (state.status === 'saved')
    return (
      <Box display='flex' align='center' gap='sm' wrap='wrap'>
        <Text variant='caption' color='muted' role='status'>
          <Trans>Saved as a LOTO report.</Trans>
        </Text>
        <Button asChild variant='outline' size='sm'>
          <Link
            to='/loto-reports/$reportId'
            params={{ reportId: state.reportId }}>
            <Trans>Open report</Trans>
          </Link>
        </Button>
      </Box>
    )
  return (
    <Box display='flex' align='center' gap='sm' wrap='wrap'>
      <Button
        type='button'
        size='sm'
        disabled={state.status === 'saving'}
        onClick={onSave}>
        {state.status === 'saving' ? (
          <Trans>Saving…</Trans>
        ) : (
          <Trans>Save as LOTO report</Trans>
        )}
      </Button>
      {state.status === 'failed' && (
        <Text variant='caption' color='destructive' role='alert'>
          <Trans>Could not save the LOTO report. Try again.</Trans>
        </Text>
      )}
    </Box>
  )
}

export function LotoProposalCard({
  scope,
  proposal,
  onOpenPage,
}: LotoProposalCardProps): ReactElement {
  const { t } = useLingui()
  const { groups } = useLotoAttachedPages(scope, proposal.attachments)
  const { state, saveProposal } = useLotoProposalSave(scope, proposal)
  const pointCount = proposal.isolation_points.length
  return (
    <ProposalShell aria-label={t`Proposed LOTO report`}>
      <Box display='flex' direction='column' gap='xs'>
        <Text as='span' variant='overline' color='primary'>
          <Trans>Proposed LOTO report</Trans>
        </Text>
        <Text as='h3' variant='subheading'>
          {proposal.name}
        </Text>
      </Box>
      {proposal.target_equipment.length > 0 && (
        <Box display='flex' direction='column' gap='xs'>
          <Text variant='caption' color='muted'>
            <Trans>Target equipment</Trans>
          </Text>
          <Text variant='label'>
            {targetEquipmentText(proposal.target_equipment)}
          </Text>
        </Box>
      )}
      <Box display='flex' direction='column' gap='xs'>
        <Text variant='caption' color='muted'>
          <Plural
            value={pointCount}
            one='# isolation point, in hang order'
            other='# isolation points, in hang order'
          />
        </Text>
        <ProposalList>
          {proposal.isolation_points.map((point) => (
            <PointItem
              key={`${point.node_id}/${point.page_id}`}
              point={point}
            />
          ))}
        </ProposalList>
      </Box>
      {groups.length > 0 && (
        <Box display='flex' direction='column' gap='xs'>
          <Text variant='caption' color='muted'>
            <Trans>Drawings</Trans>
          </Text>
          <DrawingList>
            {groups.map((group) => (
              <DrawingItem
                key={group.systemDocumentId}
                group={group}
                onOpenPage={onOpenPage}
              />
            ))}
          </DrawingList>
        </Box>
      )}
      <SaveAction state={state} onSave={() => void saveProposal()} />
    </ProposalShell>
  )
}
